/**
 * Cross-field validation — checking the label against itself.
 *
 * A nutrition panel is internally redundant: the energy figure is very nearly
 * determined by the macronutrients, the parts cannot exceed their whole, and
 * every value has a range it cannot leave. That redundancy is the only way to
 * catch an OCR error that is individually plausible. "Protein 3 g" looks fine
 * on its own; it looks wrong beside 720 kcal.
 *
 * THE RULE THIS FILE OBEYS
 * ------------------------
 * Validation FLAGS, it never REWRITES. When the arithmetic disagrees with the
 * printed calories, we do not know which one OCR got wrong — replacing the
 * printed value with the computed one would turn a visible uncertainty into an
 * invisible fabrication. The user is shown the conflict and can retake or
 * correct. That mirrors the rule the rest of this project already follows for
 * catalogue nutrition: Atwater is a contradiction test, never a source.
 */

import type { NutrientKey, NutritionLabel, ValidationIssue } from './types';

/** Atwater factors: kcal per gram. */
const KCAL_PER_G = { protein: 4, carbohydrate: 4, fat: 9 } as const;

/**
 * How far the printed energy may sit from the computed one.
 *
 * Wide on purpose. Real labels differ legitimately: fibre and polyols carry
 * energy at other factors, values are rounded independently before printing,
 * and some regions permit rounding to the nearest 10 kcal. A tight bound would
 * flag correctly-read labels constantly, and a checker that cries wolf gets
 * ignored — which costs more than it saves.
 */
const ENERGY_ABS_TOLERANCE = 60;
const ENERGY_REL_TOLERANCE = 0.3;

export function validateLabel(label: NutritionLabel): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const value = (k: NutrientKey): number | null => label.nutrients[k]?.value ?? null;

  // --- Impossible values ---------------------------------------------------
  for (const [key, nutrient] of Object.entries(label.nutrients)) {
    if (nutrient.value === null) continue;
    if (nutrient.value < 0) {
      issues.push({
        field: key as NutrientKey,
        severity: 'error',
        message: `${key} cannot be negative.`,
        reason: 'A minus sign was probably picked up from a table rule.',
      });
    }
  }

  // --- Parts cannot exceed their whole -------------------------------------
  const pairs: Array<[NutrientKey, NutrientKey, string]> = [
    ['saturatedFat', 'totalFat', 'Saturated fat is part of total fat'],
    ['transFat', 'totalFat', 'Trans fat is part of total fat'],
    ['totalSugars', 'totalCarbohydrate', 'Sugars are part of total carbohydrate'],
    ['addedSugars', 'totalSugars', 'Added sugars are part of total sugars'],
    ['dietaryFiber', 'totalCarbohydrate', 'Fibre is counted within carbohydrate on most labels'],
  ];
  for (const [part, whole, why] of pairs) {
    const p = value(part);
    const w = value(whole);
    // A whole gram of slack absorbs independent rounding of the two figures.
    if (p !== null && w !== null && p > w + 1) {
      issues.push({
        field: part,
        severity: 'warn',
        message: `${part} (${p}) is larger than ${whole} (${w}).`,
        reason: `${why}, so one of the two was misread.`,
      });
    }
  }

  // --- Macronutrients cannot exceed the food -------------------------------
  if (label.basis !== 'per_serving') {
    const protein = value('protein') ?? 0;
    const carbs = value('totalCarbohydrate') ?? 0;
    const fat = value('totalFat') ?? 0;
    const total = protein + carbs + fat;
    if (total > 105) {
      issues.push({
        field: 'label',
        severity: 'error',
        message: `Protein, carbohydrate and fat add up to ${total.toFixed(0)} g per 100 g.`,
        reason: 'That is more than the food itself weighs, so at least one value is wrong.',
      });
    }
  }

  // --- Energy against macronutrients ---------------------------------------
  const kcal = value('calories');
  const protein = value('protein');
  const carbs = value('totalCarbohydrate');
  const fat = value('totalFat');

  if (kcal !== null && protein !== null && carbs !== null && fat !== null) {
    const computed =
      protein * KCAL_PER_G.protein + carbs * KCAL_PER_G.carbohydrate + fat * KCAL_PER_G.fat;
    const drift = Math.abs(kcal - computed);
    const allowed = Math.max(ENERGY_ABS_TOLERANCE, computed * ENERGY_REL_TOLERANCE);
    if (drift > allowed) {
      issues.push({
        field: 'calories',
        severity: 'warn',
        message: `Calories (${kcal}) do not match the macronutrients, which come to about ${Math.round(computed)}.`,
        // Deliberately does not say which is wrong, because we do not know.
        reason: 'One of the four values was probably misread. Check them against the packet.',
      });
    }
  }

  // --- Unit confusion ------------------------------------------------------
  const sodium = label.nutrients.sodium;
  if (sodium?.value != null) {
    // Sodium in grams on a per-100 g basis is nearly always a misread mg.
    if (sodium.unit === 'g' && sodium.value > 5) {
      issues.push({
        field: 'sodium',
        severity: 'warn',
        message: `Sodium reads ${sodium.value} g.`,
        reason: 'Labels print sodium in milligrams; the unit was probably misread.',
      });
    }
    if (sodium.unit === 'mg' && sodium.value > 15000) {
      issues.push({
        field: 'sodium',
        severity: 'warn',
        message: `Sodium reads ${sodium.value} mg per 100 g.`,
        reason: 'That is higher than salt itself. A digit was probably added.',
      });
    }
  }

  // --- Percent read as an amount -------------------------------------------
  for (const [key, nutrient] of Object.entries(label.nutrients)) {
    if (nutrient.value === null || nutrient.dailyValue === null) continue;
    if (nutrient.value === nutrient.dailyValue) {
      issues.push({
        field: key as NutrientKey,
        severity: 'warn',
        message: `${key} amount and % daily value are identical (${nutrient.value}).`,
        reason: 'The percent column was probably read as the amount.',
      });
    }
  }

  // --- Serving size --------------------------------------------------------
  const serving = label.servingSize;
  if (serving !== null) {
    const grams = serving.grams ?? serving.milliliters ?? null;
    if (grams !== null && (grams <= 0 || grams > 2000)) {
      issues.push({
        field: 'servingSize',
        severity: 'warn',
        message: `Serving size reads ${grams}.`,
        reason: 'That is outside the range labels normally declare.',
      });
    }
  }
  if (label.basis === 'per_serving' && serving === null) {
    issues.push({
      field: 'servingSize',
      severity: 'error',
      message: 'This looks like a per-serving panel but no serving size was read.',
      reason: 'Without it the values cannot be converted to per 100 g for comparison.',
    });
  }

  return issues;
}

/**
 * Fold validation back into per-field confidence.
 *
 * An issue does not delete a value — the reader may know the label is right and
 * the checker over-eager — but it must stop a contested number from being
 * presented as if it were read cleanly.
 */
export function applyIssues(label: NutritionLabel, issues: ValidationIssue[]): NutritionLabel {
  const nutrients = { ...label.nutrients };
  for (const issue of issues) {
    if (issue.field === 'label' || issue.field === 'servingSize') continue;
    const current = nutrients[issue.field];
    if (current === undefined) continue;
    nutrients[issue.field] = {
      ...current,
      /*
       * Hard enough to actually demote the field.
       *
       * These multipliers were gentler, and a clean OCR read of 0.9 came out
       * of a failed consistency check at 0.54 — still above the trust floor,
       * so a value the checker had just contradicted was displayed as if it
       * had been read cleanly. Every issue raised here is a "confirm this
       * against the packet" case, so every one of them must land below the
       * floor from a typical starting confidence.
       */
      confidence: current.confidence * (issue.severity === 'error' ? 0.25 : 0.4),
      warnings: [...current.warnings, issue.reason],
    };
  }

  const structural = issues.filter((i) => i.field === 'label').length;
  return {
    ...label,
    nutrients,
    confidence: label.confidence * (structural > 0 ? 0.5 : 1),
  };
}

/** Below this a value is shown as unreadable rather than as a number. */
export const CONFIDENCE_FLOOR = 0.4;

export function isTrustworthy(confidence: number): boolean {
  return confidence >= CONFIDENCE_FLOOR;
}
